import { GeneralPlayer, TextInput, Button, Modal } from "..";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { toast } from "..";
import { MediaModel } from "@/database";
import {
  getMedia,
  updateIsClearFilter,
  updateIsFilter,
} from "@/store/media/media.action";
import { connect, ConnectedProps } from "react-redux";
import { GetMediaApi } from "@/database";
import { RootState } from "../../store";
import { callRegisterCustomMediaOnCms } from "@/store/upload/upload.action";
import { updateModal } from "@/store/app/app.slice";
import { setMediaDetails } from "@/store/media/media.slice";
import { getUpdateMedia } from "@/store/media/media.action";
import { UnifiedDateTimePicker } from "../DateInput/UnifiedDateTimePicker";
import { useScrollToTop } from "../../hooks/useScrollToTop";

interface ISimuliveForm {
  SlikeId: string;
  StartTime: number;
  EndTime: number;
  RepeatCount: number;
}

interface ISimulive {
  closeModal: () => void;
  slikeId?: string;
  fromDetails?: boolean;
  autoValidateOnMount?: boolean;
  onSimuliveUpdated?: (id: string) => void;
  handleSimuliveData?: (data: any) => void;
  fromList?: boolean;
  openModal?: any;
  setClonedMedia?: any;
  setFooterBtnText?: (text: string) => void;
  setIsFooterBtnDisabled?: (disabled: boolean) => void;
}

const SimuliveForm = ({
  closeModal,
  slikeId,

  callRegisterCustomMediaOnCms,
  updateIsClearFilter,
  updateIsFilter,
  updateModal,
  setMediaDetails,
  fromDetails = false,
  autoValidateOnMount = false,
  onSimuliveUpdated,
  currentMediaDetailsForEdit,
  handleSimuliveData,
  openModal,
  setClonedMedia,
  fromList = false,
  setFooterBtnText,
  setIsFooterBtnDisabled,
}: Props) => {
  const [mediaData, setMediaData] = useState<MediaModel | null>(null);
  const { navigateWithScroll } = useScrollToTop();
  const [isSimuliveDetailModalOpen, setIsSimuliveDetailModalOpen] =
    useState<boolean>(false);
  const [disableLive, setDisableLive] = useState(false);
  const [showPlayer, setShowPlayer] = useState<boolean>(false);
  const [isUrlLocked, setIsUrlLocked] = useState(false);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  const formik = useFormik<ISimuliveForm>({
    initialValues: {
      SlikeId: fromDetails
        ? currentMediaDetailsForEdit?.simulive?.parentMediaId || ""
        : slikeId || "",
      StartTime:
        currentMediaDetailsForEdit?.simulive?.startTime || new Date().getTime(),
      EndTime: currentMediaDetailsForEdit?.simulive?.endTime || 0,
      RepeatCount: currentMediaDetailsForEdit?.simulive?.repeat || 1,
    },
    validationSchema: Yup.object({
      SlikeId: Yup.string().required("Required"),
      StartTime: Yup.number().required("Required"),
      EndTime: Yup.number(),
      RepeatCount: Yup.number().min(1, "Must be at least 1"),
    }),
    onSubmit: (values) => {
      if (mediaData === null) return;
      const data = {
        parentMediaId: mediaData.id,
        startTime: values.StartTime,
        endTime: values.EndTime,
        repeat: values.RepeatCount,
      };
      setLoading(true);
      if (fromDetails) {
        handleSimuliveData?.(data);
        if (onSimuliveUpdated) {
          onSimuliveUpdated(mediaData?.id);
        }
        closeModal();
      } else {
        callRegisterCustomMediaOnCms(
          {
            type: "simulive",
            data,
            name: "media",
            title: "simulive_" + Date.now(),
            asset_type: "simulive",
            id: mediaData?.id,
            fid: `${mediaData?.id}-${Date.now().toString()}`,
          },
          (resp: any) => {
            if (resp.status === 200) {
              setMediaDetails({
                data: {
                  media: {
                    ...resp.data,
                    creditsData: mediaData?.creditsData || [],
                    parentData: mediaData.parentData || null,
                  },
                },
              });
              setClonedMedia({
                ...resp.data,
                creditsData: mediaData?.creditsData || [],
                parentData: mediaData.parentData || null,
              });
              setIsSimuliveDetailModalOpen(true);
              openModal();
            } else {
              toast.fail("");
            }
            setLoading(false);
          }
        );
      }
    },
  });

  const handleNextClick = () => {
    closeModal();
    const event = new Event("nextClick");
    window.dispatchEvent(event);
    navigateWithScroll(`/media/video`);
  };

  const handleCloseSimuliveDetailModal = () => {
    setIsSimuliveDetailModalOpen(false);
    navigateWithScroll(`/media/video`);
    closeModal();
  };

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      Object.keys(formik.initialValues).forEach((field) => {
        formik.setFieldTouched(
          field as keyof typeof formik.initialValues,
          true
        );
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      if (mediaData === null) {
        return;
      }

      formik.submitForm();
    },
    [formik, mediaData]
  );
  const handleClearUrl = () => {
    setIsUrlLocked(false);
    setShowPlayer(false);
    setMediaData(null);
    setNextButtonDisabled(true);
    setIsValidatingUrl(false);
    formik.setFieldValue("SlikeId", "");
  };

  const calEndTime = useCallback(() => {
    if (mediaData?.meta?.duration && formik.values.StartTime) {
      const duration = mediaData.meta.duration;

      const calculatedEndTime =
        formik.values.StartTime + duration * 1000 * formik.values.RepeatCount;
      formik.setFieldValue("EndTime", calculatedEndTime);
    } else {
      formik.setFieldValue("EndTime", 0);
    }
  }, [formik.values.StartTime, formik.values.RepeatCount, mediaData]);

  const getMediaData = async (debouncedSlikeId: string) => {
    setIsValidatingUrl(true);
    try {
      const res = await GetMediaApi({ id: debouncedSlikeId });

      if (
        res.status === 200 &&
        res.data.source !== "embed" &&
        res.data?.media_ref?.rel !== "simulive" &&
        res.data?.media_ref?.rel !== "chapter"
      ) {
        setMediaData(res.data);
        setShowPlayer(true);
        setIsUrlLocked(true);
        setNextButtonDisabled(false);
      } else if (res.status === 200 && res.data.source === "embed") {
        setMediaData(null);
        formik.setErrors({ SlikeId: "This is Embed id please use another id" });
        formik.setFieldTouched("SlikeId", true, false);
        formik.setFieldError(
          "SlikeId",
          "This is Embed id please use another id"
        );
      } else if (
        res.status === 200 &&
        res.data?.media_ref?.rel === "simulive"
      ) {
        setMediaData(null);
        formik.setErrors({
          SlikeId: "This is simulive id please use another id",
        });
        formik.setFieldTouched("SlikeId", true, false);
        formik.setFieldError(
          "SlikeId",
          "This is simulive id please use another id"
        );
      } else if (res.status === 200 && res.data?.media_ref?.rel === "chapter") {
        setMediaData(null);
        formik.setErrors({
          SlikeId: "This is chapter id please use another id",
        });
        formik.setFieldTouched("SlikeId", true, false);
        formik.setFieldError(
          "SlikeId",
          "This is chapter id please use another id"
        );
      } else {
        setMediaData(null);
        formik.setErrors({ SlikeId: "Invalid SlikeId" });
        formik.setFieldTouched("SlikeId", true, false);
        formik.setFieldError("SlikeId", "Invalid SlikeId");
      }
    } catch (error) {
      setMediaData(null);
      formik.setErrors({ SlikeId: "Invalid SlikeId" });
      formik.setFieldTouched("SlikeId", true, false);
      formik.setFieldError("SlikeId", "Invalid SlikeId");
    }
    setIsValidatingUrl(false);
  };

  const handleURLValidation = () => {
    getMediaData(formik.values.SlikeId);
  };

  useEffect(() => {
    if (autoValidateOnMount && !isUrlLocked) {
      handleURLValidation();
    }
  }, []);

  useEffect(() => {
    calEndTime();
  }, [formik.values.StartTime, formik.values.RepeatCount, mediaData]);

  useEffect(() => {
    const handleNextClick = () => {
      handleSubmit();
    };
    window.addEventListener("nextClickSimulive", handleNextClick);
    return () => {
      window.removeEventListener("nextClickSimulive", handleNextClick);
    };
  }, [handleSubmit]);

  useEffect(() => {
    if (
      formik.values.SlikeId.length > 0 &&
      formik.errors.SlikeId === undefined &&
      !loading &&
      !nextButtonDisabled
    ) {
      setNextButtonDisabled(false);
    } else {
      setNextButtonDisabled(true);
    }
    if (fromDetails) {
    }
  }, [
    formik.values.SlikeId,
    formik.errors.SlikeId,
    loading,
    nextButtonDisabled,
  ]);

  // Sync the footer button disabled state with the parent component
  useEffect(() => {
    if (setIsFooterBtnDisabled) {
      setIsFooterBtnDisabled(nextButtonDisabled);
    }
  }, [nextButtonDisabled, setIsFooterBtnDisabled]);

  useEffect(() => {
    if (fromDetails) {
      setFooterBtnText?.("UPDATE");
    } else {
      setFooterBtnText?.("CREATE");
    }
  }, [fromDetails]);

  return (
    <div>
      <div className="ps-1 mt-3 position-relative d-flex gap-2 mb-4 border">
        <div className="d-flex flex-grow-1 position-relative border-end p-3 flex-column">
          <div
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") {
                handleURLValidation();
              }
            }}
          >
            <TextInput
              width="w-100"
              name={"SlikeId"}
              placeholder="Slike Id"
              value={formik.values.SlikeId}
              onChange={(e) => {
                formik.handleChange(e);
                setDisableLive(false);
                formik.setFieldTouched("SlikeId", false, false);
              }}
              error={formik.errors.SlikeId}
              touched={formik.touched.SlikeId}
              label="Slike Video"
              showRequired={false}
              classNameLabel="position-absolute px-1 bg-white fs-6"
              onBlur={() => {
                formik.handleBlur("SlikeId");
                formik.setFieldTouched("SlikeId", true, false);
                handleURLValidation();
              }}
              disabled={isUrlLocked}
              endIcon={
                isValidatingUrl ? (
                  <i className="spinner-border spinner-border-sm text-primary" />
                ) : isUrlLocked ? (
                  <i
                    className="bi bi-x"
                    style={{ cursor: "pointer" }}
                    title="Clear"
                    onClick={() => {
                      handleClearUrl();
                      setDisableLive(false);
                    }}
                  />
                ) : null
              }
            />
          </div>
          <div className="rounded ratio ratio-16x9">
            {showPlayer ? (
              <>
                <GeneralPlayer id={mediaData?.id as any} autoPlay={3} />
                {loading && (
                  <div
                    className="d-flex align-items-center justify-content-center h-100 w-100 position-absolute top-50 start-50 translate-middle"
                    id="loader"
                  >
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 w-100 bgGrey rounded">
                <span>Preview</span>
              </div>
            )}
          </div>
        </div>
        <div
          className="d-flex flex-column mb-5 gap-3"
          style={{ flex: "0 0 275px" }}
        >
          <div className="row pt-2">
            <UnifiedDateTimePicker
              mode="datetime"
              onChange={(date) => {
                formik.setFieldValue("StartTime", date ? date : 0);
              }}
              value={formik.values.StartTime}
              outputFormat="timestamp"
              fromSimulive={true}
              dateLabel="Start Date"
              timeLabel="Start Time"
              width="100%"
            />
          </div>
          <div className="row">
            <UnifiedDateTimePicker
              mode="datetime"
              onChange={(date) =>
                formik.setFieldValue("EndTime", date ? date : 0)
              }
              value={formik.values.EndTime}
              outputFormat="timestamp"
              disabled={true}
              showTimeSelect={true}
              minDate={new Date(formik.values.StartTime)}
              fromSimulive={true}
              dateLabel="End Date"
              timeLabel="End Time"
              width="100%"
            />
          </div>
          <div className="px-2">
            <TextInput
              type="number"
              name="RepeatCount"
              label="Repeat Count"
              value={formik.values.RepeatCount}
              onChange={formik.handleChange}
              touched={formik.touched.RepeatCount}
              onBlur={formik.handleBlur}
              error={formik.errors.RepeatCount}
            />
          </div>
        </div>
      </div>

      {/* <div className="d-flex justify-content-end mt-2">
        <Button
          className="me-3"
          type="outline-primary"
          onClick={handleSubmit}
          title="Create/Upload Media"
          isDisabled={
            formik.values.SlikeId.length === 0 || formik.errors.SlikeId != null || loading || nextButtonDisabled
          }
        >
          {fromDetails ? 'UPDATE' : 'CREATE'}
        </Button>
      </div> */}
      {/* {isSimuliveDetailModalOpen && (
        <Modal
          isOpen={isSimuliveDetailModalOpen}
          title="Media Details"
          onClose={() => {
            updateIsClearFilter && updateIsClearFilter(true);
            updateIsFilter && updateIsFilter(false);
            handleCloseSimuliveDetailModal();
          }}
          width="1060px"
          closeOnOutsideClick={false}
          height="auto"
          footer={<Button onClick={handleNextClick}>Done</Button>}
          updateModal={updateModal}
        >
          <MediaDetails isWizard={true} closeHandler={() => handleCloseSimuliveDetailModal()} />
        </Modal>
      )} */}
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
});

const mapDispatch = {
  getMedia,
  callRegisterCustomMediaOnCms,
  updateIsClearFilter,
  updateIsFilter,
  updateModal,
  setMediaDetails,
  getUpdateMedia,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & ISimulive;

export default connector(SimuliveForm);
